import { printAddress } from '@kathondvla/sri-client/address-utils';
import { PEPPOL_METHOD } from '../../constants';

export const getInvoicingModel = (state) => {
  const ou = state.schoolManager.currentOrganisationalUnit;
  if (state.invoicing.hasErrorLoadingData) {
    return {
      isLoading: false,
      hasErrorLoadingData: true,
    };
  }
  if (!ou || !state.invoicing.initialised) {
    return {
      isLoading: true,
    };
  }
  const domain = ou.$$meta.permalink.split('/')[1];

  const vm = {
    parentLabel: ou.type === 'GOVERNINGINSTITUTION' ? {
      singular: 'bestuur',
      plural: 'besturen',
    } : {
      singular: 'organisatie',
      plural: 'organisaties',
    },
    childLabel: state.schoolManager.currentOrganisationalUnit.type === 'GOVERNINGINSTITUTION' ? {
      singular: 'instelling',
      plural: 'instellingen',
    } : {
      singular: 'onderdeel',
      plural: 'onderdelen',
    },
    isLoading: false,
    hasErrorLoadingData: false,
    parent: state.invoicing.parent,
    isPeppolEnabled: state.invoicing.peppolMethod !== PEPPOL_METHOD.NONE,
    peppolMethod: state.invoicing.peppolMethod,
    companyNumber: state.invoicing.companyNumber ? state.invoicing.companyNumber.value : null,
    companyNumberErrors: state.invoicing.companyNumberErrors,
    showLocalPeppolOptions: state.invoicing.peppolMethod === PEPPOL_METHOD.LOCAL,
    showBouwdossiersEmailField: ou.type === 'GOVERNINGINSTITUTION',
    allowDisablingPeppol: ou.type !== 'GOVERNINGINSTITUTION' || state.vm.user.isSuperAdmin,
    allowSwitchingToGlobalPeppolMethod: true, // state.invoicing.peppolMethod === PEPPOL_METHOD.LOCAL || state.invoicing.relations.length > 1,
    // filled in below by relations resources and accounting details resources
    childOus: [], // array of objects with { label and ous }
    emailGeneral: null,
    emailBouwdossiers: null,
    peppolIdentifier: null, // for the parent ou itself
    peppolIdentifierErrors: state.invoicing.identifierErrors[ou.$$meta.permalink] ? [...state.invoicing.identifierErrors[ou.$$meta.permalink]] : [],
  };

  // relations resources
  const childOusMap = {};
  state.invoicing.relations.forEach((relation) => {
    if (domain === 'sam') {
      const institution = state.invoicing.childOus[relation.to.href];
      childOusMap[relation.$$meta.permalink] = {
        href: institution.$$meta.permalink,
        type: institution.type,
        relationHref: relation.$$meta.permalink,
        name: institution.$$displayName,
        institutionNumber: state.invoicing.institutionNumbers[institution.$$meta.permalink] || null,
        address: state.invoicing.addresses[institution.$$meta.permalink]
          ? printAddress(state.invoicing.addresses[institution.$$meta.permalink].value)
          : null,
        peppolIdentifier: null, // filled in below by accounting details resources
        warnings: [{ code: 'gln.required' }],
        errors: state.invoicing.identifierErrors[relation.$$meta.permalink] || [],
      };
    } else {
      const child = state.invoicing.childOus[relation.from.href];
      childOusMap[relation.$$meta.permalink] = {
        href: child.$$meta.permalink,
        type: child.type,
        relationHref: relation.$$meta.permalink,
        name: child.$$displayName,
        address: state.invoicing.addresses[child.$$meta.permalink]
          ? printAddress(state.invoicing.addresses[child.$$meta.permalink].value)
          : null,
        peppolIdentifier: null, // filled in below by accounting details resources
        warnings: [{ code: 'gln.required' }],
        errors: state.invoicing.identifierErrors[relation.$$meta.permalink] || [],
      };
    }
  });

  // accounting details resources
  Object.values(state.invoicing.accountingDetails).forEach((detail) => {
    if (detail.type === 'EMAIL') {
      if (!detail.project) {
        vm.emailGeneral = detail.value.email;
      } else if (detail.project === 'BOUWDOSSIERS') {
        vm.emailBouwdossiers = detail.value.email;
      } else {
        console.warn(`Unknown invoicing email project for ${detail.$$meta.permalink}:`, detail.project);
      }
    } else if (detail.type === 'GLOBAL_PEPPOL_IDENTIFIER') {
      if (!detail.peppolIdentifier) {
        vm.peppolIdentifier = null;
      } else {
        vm.peppolIdentifier = state.invoicing.identifierValues[detail.organisationalUnit.href] || null;
      }
    } else if (detail.type === 'LOCAL_PEPPOL_IDENTIFIER') {
      const gln = state.invoicing.identifierValues[detail.organisationalUnitRelation.href] || null;
      childOusMap[detail.organisationalUnitRelation.href].gln = gln;
      if (gln) {
        childOusMap[detail.organisationalUnitRelation.href].warnings = [];
      }
    } 
  });

  vm.showNotAllGlnsAreFilledInWarning = state.invoicing.peppolMethod === PEPPOL_METHOD.LOCAL && Object.values(childOusMap).some(child => !child.gln);
  const schools = [];
  const otherInstitutions = [];
  const otherInstitutionTypes = new Set();
  const children = [];
  const typeMap = {
    CLB: 'CLB\'s',
    SCHOOLCOMMUNITY: 'scholengemeenschappen',
    ALGEMEEN_LEERSTEUNCENTRUM: 'leersteuncentra',
    SPECIFIEK_LEERSTEUNCENTRUM: 'leersteuncentra',
    BOARDING: 'internaten',
  }

  Object.values(childOusMap).sort((a, b) => a.name.localeCompare(b.name)).forEach((child) => {
    if (child.type === 'SCHOOL') {
      schools.push(child);
    } else if (child.href.split('/')[1] === 'sam' ) {
      otherInstitutions.push(child);
      otherInstitutionTypes.add(typeMap[child.type]);
    } else {
      children.push(child);
    }
  });
  vm.showAllGlnsNeededWarning = state.invoicing.peppolMethod === PEPPOL_METHOD.LOCAL && Object.values(childOusMap).some(child => !child.gln);

  if (schools.length) {
    vm.childOus.push({ label: 'Scholen', ous: schools });
  }
  
  if (otherInstitutions.length) {
    const label = Array.from(otherInstitutionTypes)
      .join(', ')
      .replace(/, ([^,]*)$/, ' en $1');
    vm.childOus.push({ label: label.charAt(0).toUpperCase() + label.slice(1), ous: otherInstitutions });
  }
  if (children.length) {
    vm.childOus.push({ label: 'Onderdelen', ous: children });
  }

  return vm;
};
